
import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLocalization } from '../hooks/useLocalization';
import { finalizeAfterPayment } from '../services/bookingService';
import type { FinalizeAfterPaymentResponse } from '../services/bookingService';

interface Document {
    documentType: number;
    url: string;
    name: string;
}

interface ReservationData {
    reservationNumber: string;
    hotelName: string;
    checkIn: string;
    checkOut: string;
    roomType?: string;
    boardType?: string;
    nights?: number;
    totalPrice: number;
    currency: string;
    status: string;
    bookingId?: string;
    contactEmail?: string;
    passengers?: Array<{
        type: string;
        firstName: string;
        lastName: string;
    }>;
    flights?: any[];
}

const MAX_POLL_ATTEMPTS = 12; // 12 attempts × 5 seconds = 60 seconds max
const POLL_INTERVAL_MS = 5000;

export const ConfirmationPage: React.FC = () => {
    const { t } = useLocalization();
    const navigate = useNavigate();
    const location = useLocation();
    const [reservation, setReservation] = useState<ReservationData | null>(location.state?.reservation || null);
    const [status, setStatus] = useState<'verifying' | 'processing' | 'success' | 'failed' | 'payment_received'>('verifying');
    const [errorMessage, setErrorMessage] = useState('');
    const [documents, setDocuments] = useState<Document[]>([]);
    const [pollCount, setPollCount] = useState(0);
    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hasStartedRef = useRef(false);

    useEffect(() => {
        // If reservation is passed via state (direct booking success), we are done.
        if (location.state?.reservation) {
            setStatus('success');
            return;
        }

        // Check for URL parameters from Payment Gateway Return
        const params = new URLSearchParams(location.search);
        const transactionId = params.get('tid');

        if (transactionId && !hasStartedRef.current) {
            hasStartedRef.current = true;
            finalizeBooking(transactionId);
        } else if (!transactionId && !location.state?.reservation) {
            // No state, no params
            navigate('/');
        }

        return () => {
            if (pollTimerRef.current) {
                clearTimeout(pollTimerRef.current);
            }
        };
    }, [location, navigate]);

    const getBookingContext = (): Record<string, unknown> | undefined => {
        try {
            const stored = localStorage.getItem('lumitours_booking_context');
            if (stored) {
                const parsed = JSON.parse(stored);
                return parsed;
            }
        } catch (e) {
            console.warn('Failed to read booking context from localStorage:', e);
        }
        return undefined;
    };

    const cleanupBookingContext = () => {
        try {
            localStorage.removeItem('lumitours_booking_context');
        } catch (e) {
            // Non-fatal
        }
    };

    const finalizeBooking = async (transactionId: string, attempt: number = 0) => {
        try {
            const bookingContext = attempt === 0 ? getBookingContext() : undefined;

            console.log(`🔄 Finalize attempt ${attempt + 1}/${MAX_POLL_ATTEMPTS} for tid: ${transactionId}`);
            const result: FinalizeAfterPaymentResponse = await finalizeAfterPayment(transactionId, bookingContext);

            if (result.status === 'confirmed' && result.reservation) {
                // Success! Booking saved and email sent
                setReservation(result.reservation);
                setStatus('success');
                cleanupBookingContext();
                console.log(`✅ Booking finalized: ${result.reservation.reservationNumber}`);
                return;
            }

            // Fix 5: Payment was taken but booking commit failed — show recovery message
            if (result.status === 'payment_received') {
                setStatus('payment_received');
                cleanupBookingContext();
                console.log(`⚠️ Payment received but commit pending. TID: ${transactionId}`);
                return;
            }

            if (result.status === 'processing') {
                // Payment still processing — poll again
                if (attempt + 1 >= MAX_POLL_ATTEMPTS) {
                    setStatus('failed');
                    setErrorMessage(
                        t('payment_timeout') ||
                        'Payment verification timed out. Your payment may still be processing. Please check your email for confirmation or contact support.'
                    );
                    return;
                }

                setStatus('processing');
                setPollCount(attempt + 1);

                pollTimerRef.current = setTimeout(() => {
                    finalizeBooking(transactionId, attempt + 1);
                }, POLL_INTERVAL_MS);
                return;
            }

            // Failed
            setStatus('failed');
            setErrorMessage(result.error || t('payment_failed_generic') || 'Payment failed or was cancelled.');

        } catch (e) {
            console.error('Finalize error:', e);

            // On network error, retry a few times before giving up
            if (attempt < 3) {
                pollTimerRef.current = setTimeout(() => {
                    finalizeBooking(transactionId, attempt + 1);
                }, POLL_INTERVAL_MS);
                setStatus('processing');
                return;
            }

            setStatus('failed');
            setErrorMessage(t('payment_verify_error') || 'Could not verify payment status. Please contact support.');
        }
    };

    // Verifying state (initial)
    if (status === 'verifying') {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
                <svg className="animate-spin h-10 w-10 text-black mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-gray-600 font-medium">{t('payment_verifying') || 'Verifying payment...'}</p>
            </div>
        );
    }

    // Processing state (polling)
    if (status === 'processing') {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
                <svg className="animate-spin h-10 w-10 text-black mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-gray-600 font-medium">{t('payment_processing_wait') || 'Payment is being processed...'}</p>
                <p className="text-gray-400 text-sm mt-2">{t('payment_please_wait') || 'Please wait, this may take a moment.'}</p>
                <div className="mt-4 w-48 bg-gray-200 rounded-full h-1.5">
                    <div
                        className="bg-black h-1.5 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min((pollCount / MAX_POLL_ATTEMPTS) * 100, 95)}%` }}
                    ></div>
                </div>
            </div>
        );
    }

    // Payment Received but Commit Pending (Fix 5)
    if (status === 'payment_received') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="bg-white p-10 rounded-3xl shadow-xl text-center max-w-md">
                    <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-yellow-100 mb-6">
                        <svg className="h-10 w-10 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        {t('payment_received_title') || 'Payment Received'}
                    </h2>
                    <p className="text-gray-500 mb-6">
                        {t('payment_received_message') || 'Your payment has been received. Your booking is being processed and you will receive a confirmation email shortly. If you do not receive an email within 1 hour, please contact support.'}
                    </p>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
                        <p className="text-sm text-yellow-800 font-medium">
                            📧 {t('payment_received_email_note') || 'Check your email inbox and spam folder for the confirmation.'}
                        </p>
                    </div>
                    <button onClick={() => navigate('/')} className="bg-black text-white px-6 py-3 rounded-xl font-bold">
                        {t('back_to_home') || 'Return Home'}
                    </button>
                </div>
            </div>
        );
    }

    // Failed state
    if (status === 'failed') {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="bg-white p-10 rounded-3xl shadow-xl text-center max-w-md">
                    <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-red-100 mb-6">
                        <svg className="h-10 w-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('booking_failed') || 'Booking Failed'}</h2>
                    <p className="text-gray-500 mb-6">{errorMessage}</p>
                    <button onClick={() => navigate('/')} className="bg-black text-white px-6 py-3 rounded-xl font-bold">{t('back_to_home') || 'Return Home'}</button>
                </div>
            </div>
        );
    }

    // Success state
    return (
        <div className="bg-gray-50 min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-3xl shadow-xl text-center">
                <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6">
                    <svg className="h-10 w-10 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                </div>

                <div>
                    <h2 className="mt-2 text-3xl font-extrabold text-gray-900">{t('reservation_success')}</h2>
                    <p className="mt-2 text-sm text-gray-500">{t('thank_you_message')}</p>
                </div>

                {reservation && (
                    <>
                        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 mt-8">
                            <p className="text-xs text-gray-400 uppercase tracking-wide font-bold mb-1">{t('reservation_number')}</p>
                            <p className="text-3xl font-mono font-bold text-gray-800 tracking-wider select-all">{reservation.reservationNumber}</p>
                        </div>

                        <div className="space-y-3 mt-6 text-sm">
                            <div className="flex justify-between border-b border-gray-100 pb-2">
                                <span className="text-gray-500">{t('hotels')}</span>
                                <span className="font-medium text-gray-900">{reservation.hotelName}</span>
                            </div>
                            {reservation.checkIn && (
                                <div className="flex justify-between border-b border-gray-100 pb-2">
                                    <span className="text-gray-500">{t('check_in')}</span>
                                    <span className="font-medium text-gray-900">{reservation.checkIn.split('T')[0]}</span>
                                </div>
                            )}
                            {reservation.checkOut && (
                                <div className="flex justify-between border-b border-gray-100 pb-2">
                                    <span className="text-gray-500">{t('check_out')}</span>
                                    <span className="font-medium text-gray-900">{reservation.checkOut.split('T')[0]}</span>
                                </div>
                            )}
                            {reservation.roomType && (
                                <div className="flex justify-between border-b border-gray-100 pb-2">
                                    <span className="text-gray-500">{t('room_type') || 'Room'}</span>
                                    <span className="font-medium text-gray-900">{reservation.roomType}</span>
                                </div>
                            )}
                            {reservation.boardType && (
                                <div className="flex justify-between border-b border-gray-100 pb-2">
                                    <span className="text-gray-500">{t('board_type') || 'Board'}</span>
                                    <span className="font-medium text-gray-900">{reservation.boardType}</span>
                                </div>
                            )}
                            {reservation.nights && reservation.nights > 0 && (
                                <div className="flex justify-between border-b border-gray-100 pb-2">
                                    <span className="text-gray-500">{t('nights') || 'Nights'}</span>
                                    <span className="font-medium text-gray-900">{reservation.nights}</span>
                                </div>
                            )}
                            <div className="flex justify-between border-b border-gray-100 pb-2">
                                <span className="text-gray-500">{t('total_amount')}</span>
                                <span className="font-bold text-black">{reservation.totalPrice > 0 ? `${Math.round(reservation.totalPrice).toLocaleString('sv-SE')} ${reservation.currency}` : 'Paid'}</span>
                            </div>
                            <div className="flex justify-between pb-2">
                                <span className="text-gray-500">{t('status') || 'Status'}</span>
                                <span className="font-medium text-green-600">{reservation.status}</span>
                            </div>
                        </div>

                        {/* Flight Details */}
                        {reservation.flights && reservation.flights.length > 0 && (
                            <div className="mt-6 border-t border-gray-100 pt-6">
                                <p className="text-xs text-gray-400 uppercase tracking-wide font-bold mb-4">{t('flight') || 'Flight Details'}</p>
                                <div className="space-y-4">
                                    {reservation.flights.map((flight, idx) => {
                                        const depTime = flight.departureTime ? flight.departureTime.substring(0, 5) : (flight.departureDate ? flight.departureDate.split('T')[1]?.substring(0, 5) || '—' : '—');
                                        const arrTime = flight.arrivalTime ? flight.arrivalTime.substring(0, 5) : (flight.arrivalDate ? flight.arrivalDate.split('T')[1]?.substring(0, 5) || '—' : '—');
                                        const depDateStr = flight.departureDate ? flight.departureDate.split('T')[0] : '';
                                        const arrDateStr = flight.arrivalDate ? flight.arrivalDate.split('T')[0] : '';
                                        const isOutbound = flight.direction === 'Outbound';
                                        const carrierText = flight.carrier && !flight.carrier.toLowerCase().includes('amadeus') ? flight.carrier : '';

                                        return (
                                            <div key={idx} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm relative overflow-hidden">
                                                {/* Decorative colored strip */}
                                                <div className={`absolute left-0 top-0 bottom-0 w-1 ${isOutbound ? 'bg-blue-500' : 'bg-green-500'}`}></div>
                                                
                                                <div className="flex justify-between items-center mb-4 pl-2">
                                                    <span className={`text-xs font-bold px-2 py-1 rounded-md ${isOutbound ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
                                                        {isOutbound ? '🛫 ' + (t('outbound') || 'Outbound') : '🛬 ' + (t('return_flight') || 'Return')}
                                                    </span>
                                                    <span className="text-xs font-bold text-gray-500">{carrierText} {flight.flightNumber || ''}</span>
                                                </div>

                                                <div className="flex justify-between items-center text-sm pl-2">
                                                    <div className="text-center w-24">
                                                        <div className="font-extrabold text-gray-900 text-lg">{depTime}</div>
                                                        <div className="font-bold text-teal-700 text-sm mt-0.5">{flight.departureAirport}</div>
                                                        <div className="text-gray-400 text-xs mt-1">{depDateStr}</div>
                                                    </div>

                                                    <div className="flex-1 px-4 relative h-full flex flex-col items-center justify-center">
                                                        <div className="w-full h-px bg-gray-300"></div>
                                                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white px-2">
                                                            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                {isOutbound ? (
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                                                ) : (
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                                                )}
                                                            </svg>
                                                        </div>
                                                    </div>

                                                    <div className="text-center w-24">
                                                        <div className="font-extrabold text-gray-900 text-lg">{arrTime}</div>
                                                        <div className="font-bold text-teal-700 text-sm mt-0.5">{flight.arrivalAirport}</div>
                                                        <div className="text-gray-400 text-xs mt-1">{arrDateStr}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Passenger List */}
                        {reservation.passengers && reservation.passengers.length > 0 && (
                            <div className="mt-4 border-t border-gray-100 pt-4">
                                <p className="text-xs text-gray-400 uppercase tracking-wide font-bold mb-3">{t('passengers') || 'Passengers'}</p>
                                <div className="space-y-2">
                                    {reservation.passengers.map((pax, idx) => (
                                        <div key={idx} className="flex justify-between text-sm">
                                            <span className="text-gray-500">{pax.type}</span>
                                            <span className="font-medium text-gray-900">{pax.firstName} {pax.lastName}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Email confirmation note */}
                        {reservation.contactEmail && (
                            <div className="mt-4 bg-blue-50 rounded-xl p-4 text-sm text-blue-700">
                                <p>{t('confirmation_email_sent') || `A confirmation email has been sent to ${reservation.contactEmail}`}</p>
                            </div>
                        )}
                    </>
                )}

                {/* Documents Section */}
                {documents.length > 0 && (
                    <div className="mt-8 border-t border-gray-100 pt-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-4">{t('documents') || 'Your Documents'}</h3>
                        <div className="space-y-3">
                            {documents.map((doc, idx) => (
                                <a
                                    key={idx}
                                    href={doc.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block w-full p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition flex items-center justify-between"
                                >
                                    <div className="flex items-center gap-3">
                                        <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                        <span className="font-medium text-gray-900">{doc.name || 'Document'}</span>
                                    </div>
                                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                </a>
                            ))}
                        </div>
                    </div>
                )}

                <div className="pt-6">
                    <button
                        onClick={() => navigate('/')}
                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                    >
                        {t('back_to_home')}
                    </button>
                </div>
            </div>
        </div >
    );
};
